import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  setSearchDocument,
  setSearchDocumentSourceUrl,
  setSearchIsModalMobileVisible,
  setSearchLoadingDocument,
  setSearchSelectedCitation,
  toggleSearchBookmark
} from '../../../redux/actions/searchAction'
import { backendClient } from '../../../api/backend'
import { useSnackbar } from 'notistack'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import GavelIcon from '@mui/icons-material/Gavel'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import { getCitationSectionTitle, getDocTypeColor, isMobile } from '../../../utils/utils'
import ReactGA from 'react-ga4'
import { tracker } from '../../../analytics'
import { usePdfFocus } from '../../../components/pdf-viewer/pdfContext'
import { useSearchTokens } from '../searchDesignTokens'

const JurisprudenceTitle = ({ title, country, dk }) => {
  if (!title) return null
  if (country === 'CH') {
    return (
      <Typography
        variant="caption"
        sx={{
          color: dk.text1,
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: dk.goldSub,
          padding: '2px 6px',
          borderRadius: '4px',
          border: `1px solid ${dk.goldBdr}`
        }}
      >
        {title}
      </Typography>
    )
  }
  const parts = title.split(' - ').map(part => part.split(' : '))

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {parts.map(([label, value], index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: dk.text3, fontSize: '0.75rem', fontWeight: 500, mr: 0.5 }}
          >
            {label}:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: dk.text1,
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: dk.goldSub,
              padding: '2px 6px',
              borderRadius: '4px',
              border: `1px solid ${dk.borderLight}`
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

const GroupedSearchCitation = ({ docId, citations }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const dk = useSearchTokens()
  const dispatch = useDispatch()
  const countryPref = useSelector(state => state.user?.countryPreference)
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA')
  const searchDocument = useSelector(state => state.search.searchDocument)
  const bookmarkedDocIds = useSelector(state => state.search.bookmarkedDocIds || [])
  const selectedCitation = useSelector(state => state.search.searchSelectedCitation)
  const { setPdfFocusState } = usePdfFocus()

  const [isExpanded, setIsExpanded] = useState(false)
  const titleRef = useRef(null)
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false)

  // Use first citation as representative for document-level info
  const representative = citations[0]
  const metadata = representative?.metadata || {}
  const docType = metadata.doc_type
  const docTypeColor = getDocTypeColor(docType)
  const documentExternalId = metadata.document_id || representative?.id
  const isBookmarked = bookmarkedDocIds.includes(documentExternalId)

  // Check if any section of this document is selected
  const isSelected = citations.some(c => selectedCitation?.id === c?.id)

  const resolveBlobName = (meta, preferType) => {
    const lang = meta.language || 'ar'
    const files = meta.files

    if (files) {
      if (preferType) {
        if (files[preferType]?.[lang]) return files[preferType][lang]
        if (files[preferType]?.ar) return files[preferType].ar
        if (files[preferType]) {
          const first = Object.values(files[preferType])[0]
          if (first) return first
        }
      }
      for (const type of ['html', 'pdf']) {
        if (files[type]?.[lang]) return files[type][lang]
        if (files[type]?.ar) return files[type].ar
        if (files[type]) {
          const first = Object.values(files[type])[0]
          if (first) return first
        }
      }
    }

    return null
  }

  const formatDate = dateString => {
    if (!dateString) return '-'

    const isoDateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}(|T\d{2}:\d{2}:\d{2})$/
    if (isoDateRegex.test(dateString)) {
      const date = new Date(dateString.replace(/\//g, '-'))
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    const dmyRegex = /^(\d{2})[-/]?(\d{2})[-/]?(\d{4})$/
    const dmyMatch = dateString.match(dmyRegex)
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch
      const date = new Date(`${year}-${month}-${day}`)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    const dmySlash = dateString.split('/')
    if (dmySlash.length === 3) {
      const [day, month, year] = dmySlash
      const date = new Date(`${year}-${month}-${day}`)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    return '-'
  }

  const handleBookmarkClick = (e) => {
    e.stopPropagation()
    const docTypeForBookmark = docType === 'Jurisprudence' || docType === 'case-law' ? 'case-law' : 'legislation'
    dispatch(toggleSearchBookmark(documentExternalId, docTypeForBookmark, representative))
  }

  const handleCitationClick = async (doc) => {
    // Use a composite id to guarantee uniqueness across sections of the same doc
    // This ensures ViewHtml's lastHighlightedIdRef guard doesn't block re-scrolling
    const sectionId = doc?.id || `${docId}-${doc?.metadata?.section_title || Math.random()}`
    const selectedCitation_ = {
      id: sectionId,
      metadata: doc?.metadata,
      page_content: doc?.text
    }

    console.log('[GroupedSearchCitation] handleCitationClick', {
      sectionId,
      section_title: doc?.metadata?.section_title,
      document_id: doc?.metadata?.document_id,
      hasText: !!doc?.text,
      textLength: doc?.text?.length,
      searchDocumentId: searchDocument?.id,
      isSameDoc: doc?.metadata?.document_id === searchDocument?.id,
    })

    dispatch(setSearchSelectedCitation(selectedCitation_))

    const meta = doc?.metadata || {}
    const htmlBlobName = resolveBlobName(meta, 'html')
    if (!htmlBlobName) {
      const pdfBlobName = resolveBlobName(meta, 'pdf')
      if (pdfBlobName?.toLowerCase().endsWith('.pdf')) {
        setPdfFocusState({
          document_id: meta.document_id,
          page_number: meta.page_number || 1,
          citation: {
            ...doc,
            page_content: doc?.page_content || doc?.text || '',
          },
        })
      }
    }

    if (meta.document_id !== searchDocument?.id) {
      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME
      const blobName = resolveBlobName(meta)
      if (!blobName) {
        enqueueSnackbar(t('document_fetch_error'), { variant: 'error' })
        return
      }

      try {
        dispatch(setSearchLoadingDocument(true))
        const data = await backendClient.getDocumentSas(containerName, blobName)

        if (!data || !data.blob_url_with_sas) {
          throw new Error('Invalid SAS URL response')
        }

        dispatch(setSearchDocument({
          id: meta.document_id,
          url: data.blob_url_with_sas,
          language: meta.language
        }))
        dispatch(setSearchDocumentSourceUrl(data.blob_url_with_sas))
      } catch (error) {
        dispatch(setSearchLoadingDocument(false))
        enqueueSnackbar(
          error.message.includes('Invalid SAS')
            ? `${t('document_fetch_error')} - Document not found at: ${blobName}`
            : t('document_fetch_error'),
          { variant: 'error' }
        )
        return
      }
    }

    dispatch(setSearchLoadingDocument(false))

    if (isMobile()) {
      dispatch(setSearchIsModalMobileVisible(true))
    }

    ReactGA.event({
      category: 'Citation',
      action: 'User clicked on a citation',
      label: `Document ID: ${meta.document_id}, Section Title: ${meta.section_title}`
    })
    tracker.event({
      category: 'Citation',
      action: 'User clicked on a citation',
      label: `Document ID: ${meta.document_id}, Section Title: ${meta.section_title}`
    })
  }

  useEffect(() => {
    const el = titleRef.current
    if (el) {
      setIsTitleOverflowing(el.scrollHeight > el.clientHeight)
    }
  }, [metadata.title])

  const truncateText = (text, maxLength = 100) => {
    if (!text) return ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  const handleHeaderClick = () => {
    setIsExpanded(!isExpanded)
    // Load first citation's document on expand
    if (!isExpanded) {
      handleCitationClick(representative)
    }
  }

  return (
    <Paper
      data-tour="search-citation-card"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        backgroundColor: isSelected ? dk.goldSub : dk.surface,
        border: `1px solid ${isSelected ? dk.gold : dk.border}`,
        borderRadius: '10px',
        mb: 1.5,
        transition: 'all 0.2s ease',
        position: 'relative',
        boxShadow: isSelected ? `0 0 0 1px ${dk.goldBdr}` : 'none',
        overflow: 'hidden',
        '&:hover': {
          backgroundColor: isSelected ? dk.goldSub : dk.hover,
          borderColor: isSelected ? dk.gold : dk.borderHover,
          boxShadow: dk.shadowCard,
        }
      }}
    >
      {/* Document header */}
      <Box
        onClick={handleHeaderClick}
        sx={{
          width: '100%',
          padding: '14px 16px',
          cursor: 'pointer',
        }}
      >
        {/* Top row: doc type badge + section count + bookmark */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={docType === 'Jurisprudence' || docType === 'case-law' ? <GavelIcon sx={{ fontSize: '0.8rem !important' }} /> : undefined}
              label={(() => {
                if (docType === 'Jurisprudence' || docType === 'case-law') {
                  return t(docType)
                }
                const docTypeTranslation = t(docType)
                const docNumber = metadata.doc_number
                if (docNumber) {
                  return `${docTypeTranslation} ${docNumber}`
                }
                return docTypeTranslation
              })()}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: docTypeColor,
                backgroundColor: `${docTypeColor}12`,
                border: `1px solid ${docTypeColor}35`,
                '& .MuiChip-icon': { color: docTypeColor },
                '& .MuiChip-label': { px: 1 }
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: dk.text3,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              {citations.length} {t('sections', 'sections')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleBookmarkClick}
              sx={{
                p: 0.5,
                color: isBookmarked ? dk.warning : dk.disabled,
                '&:hover': {
                  color: dk.warning,
                  backgroundColor: `${dk.warning}14`,
                }
              }}
            >
              {isBookmarked ? (
                <BookmarkIcon sx={{ fontSize: '1.1rem' }} />
              ) : (
                <BookmarkBorderIcon sx={{ fontSize: '1.1rem' }} />
              )}
            </IconButton>
            <IconButton size="small" sx={{ p: 0.5, color: dk.text3 }}>
              {isExpanded ? <ExpandLessIcon sx={{ fontSize: '1.1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
            </IconButton>
          </Box>
        </Box>

        {/* Title */}
        <Box sx={{ width: '100%', mb: 0.5 }}>
          {(docType === 'Jurisprudence' || docType === 'case-law') ? (
            <JurisprudenceTitle title={metadata.title} country={metadata.country} dk={dk} />
          ) : (
            <Tooltip title={isTitleOverflowing ? metadata.title : ''} arrow disableHoverListener={!isTitleOverflowing}>
              <Typography
                ref={titleRef}
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  lineHeight: 1.4,
                  color: dk.text1,
                  maxWidth: '100%',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {metadata.title}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* Footer metadata row */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 0.5,
            borderTop: `1px solid ${dk.borderLight}`,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayOutlinedIcon sx={{ color: dk.disabled, fontSize: '0.8rem', mr: 0.5 }} />
              <Typography
                variant='caption'
                sx={{ color: dk.text3, fontSize: '0.72rem', fontWeight: 500 }}
              >
                {(() => {
                  const fullDate = formatDate(
                    metadata.judgment_date ||
                    metadata.issue_date ||
                    metadata.publication_date
                  )
                  return fullDate !== '-' ? fullDate : (metadata.issue_year || metadata.case_year || '-')
                })()}
              </Typography>
            </Box>

            {metadata.last_update && (
              <Tooltip title={t('du_last_update')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryOutlinedIcon sx={{ color: dk.disabled, fontSize: '0.8rem', mr: 0.5 }} />
                  <Typography
                    variant='caption'
                    sx={{ color: dk.text3, fontSize: '0.72rem', fontWeight: 500 }}
                  >
                    {t('last_update_short')}: {formatDate(metadata.last_update)}
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {(metadata.court_name || metadata.jurisdiction_name) && (
              <Typography
                variant='caption'
                sx={{
                  color: dk.text3,
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  backgroundColor: dk.chipBg,
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '4px',
                }}
              >
                {metadata.court_name || metadata.jurisdiction_name}
              </Typography>
            )}
          </Box>

          {/* Source document link — hidden for case law */}
          {docType !== 'Jurisprudence' && docType !== 'case-law' && (metadata.source_url || metadata.files?.pdf || metadata.files?.html) && (
            <Typography
              component="a"
              href={metadata.source_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation()
                if (!metadata.source_url) {
                  e.preventDefault()
                }
              }}
              variant='caption'
              sx={{
                color: dk.gold,
                fontSize: '0.7rem',
                fontWeight: 500,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {metadata.source_platform || t('show_source_document')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Expandable sections */}
      <Collapse in={isExpanded} sx={{ width: '100%' }}>
        <Box sx={{
          px: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}>
          {citations.map((citation, idx) => {
            const sectionMeta = citation?.metadata || {}
            const sectionLabel = getCitationSectionTitle(sectionMeta.section_title || sectionMeta.chunk_type || '')
            const sectionId = citation?.id || `${docId}-${sectionMeta.section_title || idx}`
            const isSectionSelected = selectedCitation?.id === sectionId

            return (
              <Box
                key={citation.id || idx}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCitationClick(citation)
                }}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: `1px solid ${isSectionSelected ? dk.goldBdr : dk.borderLight}`,
                  backgroundColor: isSectionSelected ? dk.goldSub : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: isSectionSelected ? dk.goldSub : dk.hover,
                    borderColor: dk.goldBdr,
                  }
                }}
              >
                {sectionLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      color: isSectionSelected ? dk.gold : dk.text1,
                      display: 'block',
                      mb: 0.3,
                    }}
                  >
                    {sectionLabel}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: dk.text2,
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {truncateText(citation.text, 150)}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default GroupedSearchCitation
